import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { useNavLogAutoScroll } from "hooks/useNavLogAutoScroll";
import { useNavServiceLogStream } from "hooks/useNavServiceLogStream";
import { ConnectionState } from "lib/consts/ARC/connection";
import { getNav2ParamsApiBaseUrl } from "lib/nav2ParamsApi";
import { getNav2LogsStreamUrl } from "lib/nav2LogsApi";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { filterNavServiceLogLines, type NavServiceLogFilterMode } from "lib/utils/filterNavServiceLogLines";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { useARC } from "providers/ARC/ARCProvider";

const POPUP_ENABLED_STORAGE_KEY = "dev-nav-logs-popup-enabled";
const POPUP_ENABLED_EVENT = "dev-nav-logs-popup-enabled";
export const PERFORM_MOVEMENT_TOPIC = "/perform/movement";
/** Sliding window used to estimate `/perform/movement` publish rate. */
const PERFORM_MOVEMENT_HZ_WINDOW_MS = 1000;
const PERFORM_MOVEMENT_HZ_TICK_MS = 250;

export type PerformMovementTwist = {
    linearX: number;
    linearY: number;
    angularZ: number;
    /** Approximate publish rate over the last 1s window. */
    hz: number;
};

type DevNavLogsContextValue = {
    popupEnabled: boolean;
    setPopupEnabled: (enabled: boolean) => void;
    registerSettingsPanel: () => () => void;
    lines: string[];
    /** Draft text in the filter entry box (not yet added). */
    logFilterDraft: string;
    setLogFilterDraft: (value: string) => void;
    /** Active filter terms (chips). */
    logFilterItems: string[];
    addLogFilterItem: () => void;
    removeLogFilterItem: (index: number) => void;
    /** `in` = keep matches; `out` = hide matches. */
    logFilterMode: NavServiceLogFilterMode;
    setLogFilterMode: (mode: NavServiceLogFilterMode) => void;
    toggleLogFilterMode: () => void;
    connected: boolean;
    paused: boolean;
    loading: boolean;
    error: string | null;
    clear: () => void;
    togglePaused: () => void;
    /** Latest `/perform/movement` twist (linear.x, linear.y, angular.z), or null. */
    performMovement: PerformMovementTwist | null;
};

const DevNavLogsContext = createContext<DevNavLogsContextValue | null>(null);
DevNavLogsContext.displayName = "DevNavLogs";

export const useDevNavLogs = (): DevNavLogsContextValue => {
    const ctx = useContext(DevNavLogsContext);
    if (!ctx) {
        throw new Error("useDevNavLogs must be used within DevNavLogsProvider");
    }
    return ctx;
};

export const useDevNavLogsScroll = (options?: { resumeDelayMs?: number }) => {
    const { lines, logFilterItems, logFilterMode, clear: clearStream } = useDevNavLogs();
    const filteredLines = useMemo(
        () => filterNavServiceLogLines(lines, logFilterItems, logFilterMode),
        [lines, logFilterItems, logFilterMode],
    );
    const autoScroll = useNavLogAutoScroll(filteredLines, options);

    const clear = useCallback(() => {
        clearStream();
        autoScroll.resetAutoScroll();
    }, [autoScroll, clearStream]);

    return {
        lines: filteredLines,
        clear,
        scrollRef: autoScroll.scrollRef,
        onScroll: autoScroll.onScroll,
    };
};

const readPopupEnabled = (): boolean => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(POPUP_ENABLED_STORAGE_KEY) === "true";
};

const parsePerformMovementTwist = (
    data: unknown,
    hz: number,
): PerformMovementTwist | null => {
    if (data == null || typeof data !== "object") {
        return null;
    }
    const msg = data as {
        linear?: { x?: unknown; y?: unknown };
        angular?: { z?: unknown };
    };
    const linearX = Number(msg.linear?.x);
    const linearY = Number(msg.linear?.y);
    const angularZ = Number(msg.angular?.z);
    if (![linearX, linearY, angularZ].every(Number.isFinite)) {
        return null;
    }
    return { linearX, linearY, angularZ, hz };
};

const hzFromTimestamps = (timestamps: number[], now: number): number => {
    const windowStart = now - PERFORM_MOVEMENT_HZ_WINDOW_MS;
    let count = 0;
    for (let i = timestamps.length - 1; i >= 0; i -= 1) {
        if (timestamps[i] < windowStart) break;
        count += 1;
    }
    return count;
};

type Props = {
    children: ReactNode;
};

const DevNavLogsProvider = ({ children }: Props) => {
    const { selectedARC, controlSocket, connectionState } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [arcId, isSecure, selectedARC?.host],
    );
    const streamUrl = useMemo(
        () => getNav2LogsStreamUrl(selectedARC?.host ?? null, isSecure, arcId),
        [arcId, isSecure, selectedARC?.host],
    );

    const [popupEnabled, setPopupEnabledState] = useState(readPopupEnabled);
    const [settingsPanelConsumers, setSettingsPanelConsumers] = useState(0);
    const [performMovement, setPerformMovement] = useState<PerformMovementTwist | null>(null);
    const performMovementTimestampsRef = useRef<number[]>([]);
    const lastPerformMovementRef = useRef<Omit<PerformMovementTwist, "hz"> | null>(null);
    const [logFilterDraft, setLogFilterDraft] = useState("");
    const [logFilterItems, setLogFilterItems] = useState<string[]>([]);
    const [logFilterMode, setLogFilterMode] = useState<NavServiceLogFilterMode>("in");

    const pruneAndPublishMovementHz = useCallback(() => {
        const now = performance.now();
        const windowStart = now - PERFORM_MOVEMENT_HZ_WINDOW_MS;
        performMovementTimestampsRef.current = performMovementTimestampsRef.current.filter(
            (t) => t >= windowStart,
        );
        const last = lastPerformMovementRef.current;
        if (!last) {
            setPerformMovement(null);
            return;
        }
        const hz = hzFromTimestamps(performMovementTimestampsRef.current, now);
        setPerformMovement({ ...last, hz });
    }, []);

    const addLogFilterItem = useCallback(() => {
        const term = logFilterDraft.trim();
        if (!term) return;
        setLogFilterItems((prev) =>
            prev.some((item) => item.toLowerCase() === term.toLowerCase())
                ? prev
                : [...prev, term],
        );
        setLogFilterDraft("");
    }, [logFilterDraft]);

    const removeLogFilterItem = useCallback((index: number) => {
        setLogFilterItems((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const toggleLogFilterMode = useCallback(() => {
        setLogFilterMode((mode) => (mode === "in" ? "out" : "in"));
    }, []);

    const setPopupEnabled = useCallback((enabled: boolean) => {
        setPopupEnabledState(enabled);
        if (typeof window !== "undefined") {
            window.localStorage.setItem(POPUP_ENABLED_STORAGE_KEY, String(enabled));
            window.dispatchEvent(
                new CustomEvent(POPUP_ENABLED_EVENT, { detail: enabled }),
            );
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const syncFromStorage = () => {
            setPopupEnabledState(readPopupEnabled());
        };

        const onPopupEnabledEvent = (event: Event) => {
            const enabled = (event as CustomEvent<boolean>).detail;
            if (typeof enabled === "boolean") {
                setPopupEnabledState(enabled);
            }
        };

        window.addEventListener("storage", syncFromStorage);
        window.addEventListener(POPUP_ENABLED_EVENT, onPopupEnabledEvent as EventListener);

        return () => {
            window.removeEventListener("storage", syncFromStorage);
            window.removeEventListener(POPUP_ENABLED_EVENT, onPopupEnabledEvent as EventListener);
        };
    }, []);

    const registerSettingsPanel = useCallback(() => {
        setSettingsPanelConsumers((count) => count + 1);
        return () => setSettingsPanelConsumers((count) => Math.max(0, count - 1));
    }, []);

    const streamEnabled = popupEnabled || settingsPanelConsumers > 0;
    const stream = useNavServiceLogStream({
        baseUrl,
        streamUrl,
        enabled: streamEnabled,
    });

    useEffect(() => {
        if (
            !streamEnabled ||
            connectionState !== ConnectionState.Connected ||
            !controlSocket
        ) {
            performMovementTimestampsRef.current = [];
            lastPerformMovementRef.current = null;
            setPerformMovement(null);
            return;
        }

        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            PERFORM_MOVEMENT_TOPIC,
            (data) => {
                const now = performance.now();
                const windowStart = now - PERFORM_MOVEMENT_HZ_WINDOW_MS;
                performMovementTimestampsRef.current.push(now);
                performMovementTimestampsRef.current =
                    performMovementTimestampsRef.current.filter((t) => t >= windowStart);
                const hz = hzFromTimestamps(performMovementTimestampsRef.current, now);
                const parsed = parsePerformMovementTwist(data, hz);
                if (parsed) {
                    lastPerformMovementRef.current = {
                        linearX: parsed.linearX,
                        linearY: parsed.linearY,
                        angularZ: parsed.angularZ,
                    };
                    setPerformMovement(parsed);
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                markMessageReceived: (data) =>
                    parsePerformMovementTwist(data, 0) !== null,
            },
        );

        const hzTick = window.setInterval(
            pruneAndPublishMovementHz,
            PERFORM_MOVEMENT_HZ_TICK_MS,
        );

        return () => {
            cleanup();
            window.clearInterval(hzTick);
            performMovementTimestampsRef.current = [];
            lastPerformMovementRef.current = null;
            setPerformMovement(null);
        };
    }, [connectionState, controlSocket, pruneAndPublishMovementHz, streamEnabled]);

    const value = useMemo<DevNavLogsContextValue>(
        () => ({
            popupEnabled,
            setPopupEnabled,
            registerSettingsPanel,
            lines: stream.lines,
            logFilterDraft,
            setLogFilterDraft,
            logFilterItems,
            addLogFilterItem,
            removeLogFilterItem,
            logFilterMode,
            setLogFilterMode,
            toggleLogFilterMode,
            connected: stream.connected,
            paused: stream.paused,
            loading: stream.loading,
            error: stream.error,
            clear: stream.clear,
            togglePaused: stream.togglePaused,
            performMovement,
        }),
        [
            addLogFilterItem,
            logFilterDraft,
            logFilterItems,
            logFilterMode,
            performMovement,
            popupEnabled,
            registerSettingsPanel,
            removeLogFilterItem,
            setPopupEnabled,
            stream.clear,
            stream.connected,
            stream.error,
            stream.lines,
            stream.loading,
            stream.paused,
            stream.togglePaused,
            toggleLogFilterMode,
        ],
    );

    return <DevNavLogsContext.Provider value={value}>{children}</DevNavLogsContext.Provider>;
};

export default DevNavLogsProvider;
